/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable, InjectionToken } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  BookableSlot,
  BookingDetails,
  BookingFormDetails,
  BookingFormStatus,
  BookingFormUpsertRequest,
  PlaceBookingRequest,
  SuggestTimeRequest,
} from '../../models/bookings/v3-booking.model';
import { CursorPage } from '../../models/page';
import { ObservableResult } from '../../store';
import { RestClientService } from '../rest-client.service';

export interface BookingPageOptions {
  cursor?: string | null;
  size?: number;
  ascending?: boolean;
}

const pageQuery = (options?: BookingPageOptions): string => {
  if (!options) return '';
  const parts = [
    options.cursor ? `cursor=${encodeURIComponent(options.cursor)}` : '',
    options.size ? `size=${options.size}` : '',
    options.ascending === false ? 'ascending=false' : '',
  ].filter(Boolean);
  return parts.length ? `&${parts.join('&')}` : '';
};

export interface V3BookingDataService {
  getEmbeddableForms(): ObservableResult<BookingFormDetails[]>;

  getFormsByUids(uids: string[]): ObservableResult<BookingFormDetails[]>;

  createForm(request: BookingFormUpsertRequest): ObservableResult<BookingFormDetails>;

  updateForm(bookingFormUid: string, request: BookingFormUpsertRequest): ObservableResult<BookingFormDetails>;

  deleteForm(bookingFormUid: string): ObservableResult<boolean>;

  changeFormStatus(bookingFormUid: string, status: BookingFormStatus): ObservableResult<BookingFormDetails>;

  getHostedBookings(from: string, to: string, options?: BookingPageOptions): ObservableResult<CursorPage<BookingDetails>>;

  getMyBookings(from: string, to: string, options?: BookingPageOptions): ObservableResult<CursorPage<BookingDetails>>;

  getPendingRequests(): ObservableResult<BookingDetails[]>;

  getMyForms(): ObservableResult<BookingFormDetails[]>;

  getBooking(bookingUid: string): ObservableResult<BookingDetails>;

  getBookingIcs(bookingUid: string): Observable<string>;

  getSlots(bookingFormUid: string, from: string, to: string): ObservableResult<BookableSlot[]>;

  placeBooking(bookingFormUid: string, request: PlaceBookingRequest): ObservableResult<BookingDetails>;

  acceptBooking(bookingUid: string): ObservableResult<BookingDetails>;

  declineBooking(bookingUid: string, reason?: string): ObservableResult<BookingDetails>;

  reschedule(bookingUid: string, request: SuggestTimeRequest): ObservableResult<BookingDetails>;

  suggestTime(bookingUid: string, request: SuggestTimeRequest): ObservableResult<BookingDetails>;

  claimBooking(bookingUid: string): ObservableResult<BookingDetails>;

  cancelBooking(bookingUid: string): ObservableResult<BookingDetails>;
}

export const V3_BOOKING_DATA_SERVICE = new InjectionToken<V3BookingDataService>('[Bookings] V3BookingDataService');

@Injectable()
export class ApiV3BookingDataService implements V3BookingDataService {
  private readonly root = 'booking-forms';
  private readonly apiRoot = environment.localServiceOverrides?.resourcesBaseUrl || environment.apiRootUrl || '';

  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getEmbeddableForms(): ObservableResult<BookingFormDetails[]> {
    return this.get<BookingFormDetails[]>(`${this.root}/embeddable`, 'getMyForms');
  }

  getFormsByUids(uids: string[]): ObservableResult<BookingFormDetails[]> {
    return this.get<BookingFormDetails[]>(`${this.root}/by-uids?uids=${uids.join(',')}`, 'getMyForms');
  }

  createForm(request: BookingFormUpsertRequest): ObservableResult<BookingFormDetails> {
    return this.postWrapped<BookingFormDetails>(this.root, request, 'createForm');
  }

  updateForm(bookingFormUid: string, request: BookingFormUpsertRequest): ObservableResult<BookingFormDetails> {
    return this.putWrapped<BookingFormDetails>(`${this.root}/${bookingFormUid}`, request, 'updateForm');
  }

  changeFormStatus(bookingFormUid: string, status: BookingFormStatus): ObservableResult<BookingFormDetails> {
    return this.putWrapped<BookingFormDetails>(`${this.root}/${bookingFormUid}/status`, { status }, 'changeFormStatus');
  }

  deleteForm(bookingFormUid: string): ObservableResult<boolean> {
    return this.client.delete<{ content: boolean }>(Location.joinWithSlash(this.apiRoot, `${this.root}/${bookingFormUid}`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body?.content ?? false)),
      catchError(() => ObservableResult.ofError(this.errorMessage('deleteForm'))),
    );
  }

  getHostedBookings(from: string, to: string, options?: BookingPageOptions): ObservableResult<CursorPage<BookingDetails>> {
    return this.get<CursorPage<BookingDetails>>(
      `${this.root}/bookings/hosted?from=${from}&to=${to}${pageQuery(options)}`,
      'getHostedBookings',
    );
  }

  getMyBookings(from: string, to: string, options?: BookingPageOptions): ObservableResult<CursorPage<BookingDetails>> {
    return this.get<CursorPage<BookingDetails>>(`${this.root}/bookings/mine?from=${from}&to=${to}${pageQuery(options)}`, 'getMyBookings');
  }

  getPendingRequests(): ObservableResult<BookingDetails[]> {
    return this.get<BookingDetails[]>(`${this.root}/bookings/pending`, 'getPendingRequests');
  }

  getMyForms(): ObservableResult<BookingFormDetails[]> {
    return this.get<BookingFormDetails[]>(`${this.root}/mine`, 'getMyForms');
  }

  getBooking(bookingUid: string): ObservableResult<BookingDetails> {
    return this.get<BookingDetails>(`${this.root}/bookings/${bookingUid}`, 'getMyBookings');
  }

  getBookingIcs(bookingUid: string): Observable<string> {
    return this.client
      .post<string>(Location.joinWithSlash(this.apiRoot, `${this.root}/bookings/${bookingUid}/export/ics`), {}, null, null, 'text')
      .pipe(map(({ body }) => body as unknown as string));
  }

  getSlots(bookingFormUid: string, from: string, to: string): ObservableResult<BookableSlot[]> {
    return this.get<BookableSlot[]>(`${this.root}/${bookingFormUid}/slots?from=${from}&to=${to}`, 'getSlots');
  }

  placeBooking(bookingFormUid: string, request: PlaceBookingRequest): ObservableResult<BookingDetails> {
    return this.post<BookingDetails>(`${this.root}/${bookingFormUid}/bookings`, request, 'placeBooking');
  }

  acceptBooking(bookingUid: string): ObservableResult<BookingDetails> {
    return this.post<BookingDetails>(`${this.root}/bookings/${bookingUid}/accept`, {}, 'acceptBooking');
  }

  declineBooking(bookingUid: string, reason?: string): ObservableResult<BookingDetails> {
    return this.post<BookingDetails>(`${this.root}/bookings/${bookingUid}/decline`, { reason }, 'declineBooking');
  }

  reschedule(bookingUid: string, request: SuggestTimeRequest): ObservableResult<BookingDetails> {
    return this.post<BookingDetails>(`${this.root}/bookings/${bookingUid}/reschedule`, request, 'reschedule');
  }

  suggestTime(bookingUid: string, request: SuggestTimeRequest): ObservableResult<BookingDetails> {
    return this.post<BookingDetails>(`${this.root}/bookings/${bookingUid}/suggest-time`, request, 'suggestTime');
  }

  claimBooking(bookingUid: string): ObservableResult<BookingDetails> {
    return this.post<BookingDetails>(`${this.root}/bookings/${bookingUid}/claim`, {}, 'claimBooking');
  }

  cancelBooking(bookingUid: string): ObservableResult<BookingDetails> {
    return this.post<BookingDetails>(`${this.root}/bookings/${bookingUid}/cancel`, {}, 'cancelBooking');
  }

  private get<T>(path: string, errorKey: string): ObservableResult<T> {
    return this.client.get<T>(Location.joinWithSlash(this.apiRoot, path)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.errorMessage(errorKey))),
    );
  }

  private post<T>(path: string, request: unknown, errorKey: string): ObservableResult<T> {
    return this.client.post<T>(Location.joinWithSlash(this.apiRoot, path), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.errorMessage(errorKey))),
    );
  }

  private postWrapped<T>(path: string, request: unknown, errorKey: string): ObservableResult<T> {
    return this.client.post<{ content: T }>(Location.joinWithSlash(this.apiRoot, path), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body?.content as T)),
      catchError(() => ObservableResult.ofError(this.errorMessage(errorKey))),
    );
  }

  private putWrapped<T>(path: string, request: unknown, errorKey: string): ObservableResult<T> {
    return this.client.put<{ content: T }>(Location.joinWithSlash(this.apiRoot, path), request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body?.content as T)),
      catchError(() => ObservableResult.ofError(this.errorMessage(errorKey))),
    );
  }

  private errorMessage(errorKey: string): string {
    return this.translocoService.translate(`translations.bookings.v3.errors.${errorKey}`);
  }
}
